<template>
  <tela-component title="Visita Técnica">
    <!-- Overlay enquanto ajusta configurações -->
    <v-overlay
      v-if="configuracoes.carregando || sincronizando || loading || visitas.carregando"
      opacity="0.8"
      :z-index="2000"
    >
      <div class="d-flex flex-column align-center justify-center text-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
          width="6"
          class="mb-4"
        />
        <div class="text-h6 font-weight-medium mb-2">
          {{ sincronizando ? produtores.msgSync :  visitas.carregando ? "Carregando visitas" : "Ajustando configurações" }}
        </div>
        <div class="text-body-2 grey--text">Aguarde um momento...</div>
      </div>
    </v-overlay>

    <template v-else-if="configuracoes.dados">
      <!-- Barra de ações única (desktop + mobile) -->
      <v-row dense class="align-center pt-0">
        <v-col cols="12" md="auto" class="mb-2 mb-md-0" align-self="center">
          <v-btn
            color="primary"
            @click="irRotaSelecionarProdutor()"
            v-if="!isMobile()"
          >
            <v-icon left>mdi-plus</v-icon>
            Adicionar visita
          </v-btn>
        </v-col>

        <v-col cols="7" sm="6" md="6" align-self="center">
          <v-text-field
            label="Pesquisar"
            placeholder="Pesquise por nome ou matrícula"
            clearable
            prepend-inner-icon="mdi-magnify"
            v-model="pesquisa"
          />
        </v-col>

        <v-col cols="3" sm="2" md="auto" align-self="center">
          <btn-filtro-visitas />
        </v-col>

        <v-col cols="2" md="auto" align-self="center" v-if="isMobile()">
          <v-btn
            title="Sincronizar dados"
            to="/visita-tecnica/sincronizar"
            small
          >
            <v-icon>mdi-cloud-sync-outline</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Lista/Estados (único bloco) -->
      <v-scale-transition>
        <!-- 1) Carregando e sem itens: skeletons -->
        <v-sheet
          v-if="isLoading && !hasItems"
          class="rounded-lg mt-2"
          elevation="0"
          color="transparent"
          role="region"
          aria-label="Carregando visitas"
        >
          <v-list color="transparent" class="py-0">
            <v-skeleton-loader
              v-for="n in skeletonCount"
              :key="`skl-${n}`"
              type="list-item-avatar-two-line"
              class="mb-2 rounded-lg"
            />
          </v-list>
        </v-sheet>

        <!-- 2) Tem itens -->
        <v-sheet
          v-else-if="hasItems"
          class="rounded-lg mt-2"
          elevation="0"
          color="transparent"
          role="region"
          aria-label="Lista de visitas"
        >
          <v-progress-linear
            v-if="isLoading"
            indeterminate
            color="primary"
            height="3"
            class="rounded-t-lg mb-2"
          />

          <!-- Mobile: container rolável simples (sem <v-virtual-scroll>) -->
          <div v-if="isMobile()" :style="estiloScrollMobile">
            <tile-visita
              v-for="(item, i) in visitasFiltradas"
              :key="`vtm-${item.id || i}`"
              :visita="item"
              class="mb-2"
            />
          </div>

          <!-- Desktop: lista normal com rolagem no container -->
          <v-list v-else color="transparent" class="py-0 tile-scroll-desktop">
            <tile-visita
              v-for="(visita, i) in visitasFiltradas"
              :key="`vt-${visita.id || i}`"
              :visita="visita"
              class="mb-2"
            />
          </v-list>

          <!-- Quando há itens no total, mas a busca não retornou nada -->
          <v-container
            v-if="hasItems && visitasFiltradas.length === 0 && !isLoading"
            class="text-center grey--text text--darken-1 py-10"
          >
            <v-icon size="48" color="grey lighten-1" class="mb-2"
              >mdi-file-search-outline</v-icon
            >
            <div class="subtitle-1 font-weight-medium">
              Nenhuma visita encontrada
            </div>
            <div class="caption grey--text">
              Tente buscar por outro nome ou matrícula.
            </div>
          </v-container>
        </v-sheet>

        <!-- 3) Vazio (não há nenhum item no sistema) -->
        <v-sheet
          v-else
          class="mt-4 pa-8 text-center rounded-xl d-flex flex-column align-center justify-center empty-vt"
          color="grey lighten-5"
          elevation="0"
        >
          <v-icon size="64" class="mb-4" color="primary darken-1"
            >mdi-map-search-outline</v-icon
          >
          <div class="text-h6 font-weight-medium mb-1">
            Nenhuma visita por aqui
          </div>
          <div class="text-body-2">
            {{
              isMobile()
                ? "Toque no botão “+” para criar a primeira visita técnica."
                : "Clique em “Adicionar visita” para criar a primeira visita técnica."
            }}
          </div>
          <!-- <div class="mt-4">
          <v-btn color="primary" @click="irRotaSelecionarProdutor">
            <v-icon left>mdi-plus</v-icon> Adicionar visita
          </v-btn>
        </div> -->
        </v-sheet>
      </v-scale-transition>

      <!-- FAB apenas no mobile -->
      <v-btn
        v-if="isMobile() && !sincronizando"
        fab
        fixed
        bottom
        right
        large
        color="primary"
        @click="irRotaSelecionarProdutor()"
        aria-label="Adicionar visita técnica"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>

      <router-view />
    </template>
  </tela-component>
</template>

<script>
import TelaComponent from "@/components/tela/TelaComponent.vue";
import TileVisita from "@/components/visita-tecnica/TileVisita.vue";
import BtnFiltroVisitas from "@/components/visita-tecnica/BtnFiltroVisitas.vue";
import { mapActions, mapMutations, mapState } from "vuex";
import moment from "moment";

function normalizar(str) {
  return (str || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

export default {
  name: "VisitaTecnica",
  components: {
    TelaComponent,
    TileVisita,
    BtnFiltroVisitas,
  },
  data: () => ({
    pesquisa: "",
    loading: true,
  }),
  computed: {
    ...mapState("visitaTecnica", [
      "visitas",
      "configuracoes",
      "sincronizando",
      "produtores",
    ]),
    isLoading() {
      return !!this.visitas?.carregando;
    },

    hasItems() {
      return (
        Array.isArray(this.visitas?.itens) && this.visitas.itens.length > 0
      );
    },

    skeletonCount() {
      return this.isMobile() ? 5 : 7;
    },

    // Altura do container rolável no mobile
    alturaVirtual() {
      const reservaCabecalho = this.isMobile()
        ? screen.availHeight * 0.24
        : 295;
      return Math.max(300, window.innerHeight - reservaCabecalho);
    },

    estiloScrollMobile() {
      return {
        maxHeight: this.alturaVirtual + "px",
        overflowY: "auto",
        paddingRight: "4px",
        scrollBehavior: "smooth",
      };
    },

    // Busca por nome OU matrícula (ignora acentos) e aceita ID
    visitasFiltradas() {
      const itens = this.visitas?.itens || [];
      const q = normalizar(this.pesquisa);

      // cria uma cópia para não mutar o state
      const ordenados = [...itens].sort((a, b) => {
        const da = moment(a.data_visita, "DD/MM/YYYY HH:mm:ss", true);
        const db = moment(b.data_visita, "DD/MM/YYYY HH:mm:ss", true);

        // primeiro: data (mais recente primeiro)
        if (da.isBefore(db)) return 1;
        if (da.isAfter(db)) return -1;

        // se a data for igual: id (maior primeiro)
        const idA = a.id || 0;
        const idB = b.id || 0;
        return idB - idA;
      });

      // sem pesquisa: só retorna ordenado
      if (!q) return ordenados;

      // com pesquisa: filtra em cima da lista já ordenada
      return ordenados.filter((v) => {
        const nome = normalizar(v?.nome_produtor);
        const matr = normalizar(v?.matricula || v?.matr || "");
        const idStr = String(v?.id || "");
        return nome.includes(q) || matr.includes(q) || idStr.includes(q);
      });
    },
  },
  methods: {
    ...mapMutations("visitaTecnica", ["resetVisita"]),
    ...mapActions("visitaTecnica", [
      "buscarVisitas",
      "buscarConfiguracoes",
      "sincronizarDados",
      "precisaSincronizar",
      "sincronizarVisitas",
      "atualizarCacheOffline",
    ]),

    isMobile() {
      return this.$mq === "sm";
    },

    irRotaSelecionarProdutor() {
      this.$router.push({ path: "visita-tecnica/selecionar-produtor" });
    },

    _onResize() {
      this.$forceUpdate();
    },
  },
  async mounted() {
    this.loading = true;
    this.resetVisita();
    await this.buscarConfiguracoes();
    const syncData = await this.precisaSincronizar();
    if (syncData && this.isMobile()) {
      await this.sincronizarDados();
    } else {
      if (this.isMobile()) {
        await this.sincronizarVisitas();
      }
    }
    await this.buscarVisitas();
    this.loading = false;
    window.addEventListener("resize", this._onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this._onResize);
  },
};
</script>

<style>
/* Desktop: lista com rolagem suave */
.tile-scroll-desktop {
  max-height: calc(100vh - 295px);
  overflow-y: auto;
  padding-right: 4px; /* espaço p/ scroll */
  scroll-behavior: smooth;
}

/* Scrollbar discreto (WebKit) */
.tile-scroll-desktop::-webkit-scrollbar {
  width: 8px;
}
.tile-scroll-desktop::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}
.tile-scroll-desktop::-webkit-scrollbar-track {
  background: transparent;
}
</style>
